<template>
  <v-dialog v-model="showDialog" max-width="1300" scrollable persistent>
    <v-card>
      <v-card-title class="secondary white--text px-4 py-2">
        <span>Edit Charges</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-form ref="chargesForm" v-model="isValid">
          <v-row class="ma-0">
            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <MultiSelect
                label="Select Transporter *"
                :items="transporterList"
                itemText="company_title"
                itemValue="id"
                hide-details="auto"
                :readonly="readonly"
                :rules="[(val) => !!val || 'Transporter is required']"
                v-model="localDetails.transporter"
                :error-messages="formErrors.transporter"
                @change="
                  delete formErrors.transporter;
                  checkVatApplicablity();
                "
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                :label="`Transport Charge (${$globals.currency}) *`"
                hide-details="auto"
                :clearable="false"
                :readonly="readonly"
                v-model="localDetails.rate"
                :rules="[(val) => !!val || 'Rate is required']"
                :error-messages="formErrors.rate"
                @input="
                  delete formErrors.rate;
                  setAdditionalChargeDetails();
                "
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                label="Tax(%) *"
                class="grey lighten-3"
                hide-details="auto"
                :value="Number(localDetails.tax_percentage || 0).toFixed(2)"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                :label="`Additional Charge (${$globals.currency})`"
                class="grey lighten-3"
                hide-details="auto"
                :clearable="false"
                v-model="transportTotalAdditionalCharge"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                :label="`Total Cost (${$globals.currency})`"
                class="grey lighten-3"
                hide-details="auto"
                :clearable="false"
                :value="localDetails.total_cost"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                :label="`Total Taxable Amount (${$globals.currency})`"
                class="grey lighten-3"
                :clearable="false"
                hide-details="auto"
                :value="localDetails.total_taxable_amount"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                :label="`Total Cost With Tax (${$globals.currency})`"
                :clearable="false"
                class="grey lighten-3"
                hide-details="auto"
                :value="localDetails.total_cost_with_tax"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                :label="`Margin Rate (${$globals.currency})`"
                class="grey lighten-3"
                hide-details="auto"
                :value="marginRate"
                :clearable="false"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                readonly
                label="Margin Percentage*"
                class="grey lighten-3"
                :clearable="false"
                hide-details="auto"
                :value="marginPercent"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="text"
                label="Reason For Sub Hire*"
                :readonly="readonly"
                :rules="[(val) => !!val || 'Reason is required']"
                hide-details="auto"
                v-model="localDetails.reason_for_sub_hire"
                :error-messages="formErrors.reason_for_sub_hire"
                :clearable="false"
                @input="delete formErrors.reason_for_sub_hire"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <v-switch
                class="mt-1"
                hide-details="auto"
                label="Cargo liability insurance"
                :readonly="readonly"
                true-value="Yes"
                false-value="No"
                v-model="localDetails.cargo_liability_insurance"
              ></v-switch>
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <v-switch
                class="mt-1"
                hide-details="auto"
                label="General third party insurance"
                :readonly="readonly"
                true-value="Yes"
                false-value="No"
                v-model="localDetails.general_third_party_insurance"
              ></v-switch>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="d-flex justify-end">
        <v-btn text @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" depressed :disabled="!isValid" @click="save">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

export default {
  components: {
    InputField,
    MultiSelect,
  },

  props: {
    value: { type: Boolean, default: false },
    subHireFormDetails: { type: Object, required: true },
    legCount: { type: Number, default: 0 },
    totalRevenueWithAdditionalCharge: { type: Number, default: 0 },
    readonly: { type: Boolean, default: false },
    laneServiceType: { default: null },
  },

  data() {
    return {
      localDetails: {
        cargo_liability_insurance: "No",
        general_third_party_insurance: "No",
        sub_hire_job_charges: [],
      },
      transporterList: [],
      vendorVatApply: "Yes",
      transportTotalAdditionalCharge: 0,
      formErrors: {},
      isValid: true,
    };
  },

  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    marginRate() {
      return (
        parseFloat(this.totalRevenueWithAdditionalCharge || 0) -
        parseFloat(this.localDetails.total_cost || 0)
      ).toFixed(2);
    },
    marginPercent() {
      if (!parseFloat(this.totalRevenueWithAdditionalCharge)) return "0.00";
      return (
        (parseFloat(this.marginRate) / parseFloat(this.totalRevenueWithAdditionalCharge)) *
        100
      ).toFixed(2);
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.localDetails = JSON.parse(JSON.stringify(this.subHireFormDetails));
        if (!this.localDetails.sub_hire_job_charges) {
          this.localDetails.sub_hire_job_charges = [];
        }
        this.formErrors = {};
        this.getTransporterList(() => this.checkVatApplicablity());
        this.setAdditionalChargeDetails();
      }
    },
  },

  methods: {
    closeDialog() {
      this.showDialog = false;
    },

    getTransporterList(callback = null) {
      this.$bus.$emit("showLoader", true);
      this.$api.company
        .getCompanySelectList({ company_type: "Transporter", limit: "all", is_active: true })
        .then((res) => {
          this.transporterList = res.data;
          this.$bus.$emit("showLoader", false);
          if (callback) callback();
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    checkVatApplicablity() {
      if (!this.transporterList || this.transporterList.length === 0) return;
      if (!this.localDetails.transporter) return;
      const selectedTransporter = this.transporterList.find(
        (t) => t.id == this.localDetails.transporter
      );
      if (!selectedTransporter) return;

      this.vendorVatApply = selectedTransporter.vat_applicability;
      if (this.vendorVatApply == "No") {
        this.localDetails.tax_percentage = 0.0;
      } else if (this.laneServiceType == "local") {
        this.localDetails.tax_percentage = 5.0;
      } else if (this.laneServiceType == "Cross Border") {
        this.localDetails.tax_percentage = 0.0;
      }
      this.setAdditionalChargeDetails();
    },

    setAdditionalChargeDetails() {
      this.transportTotalAdditionalCharge = 0.0;
      this.localDetails.sub_hire_job_charges.forEach((charge) => {
        if (charge.rate) {
          this.transportTotalAdditionalCharge += parseFloat(parseFloat(charge.rate).toFixed(2));
        }
      });

      this.localDetails.total_cost = (
        (parseFloat(this.localDetails.rate || 0) +
          parseFloat(this.transportTotalAdditionalCharge)) *
        this.legCount
      ).toFixed(2);

      this.setTotalTaxableAmount();
    },

    setTotalTaxableAmount() {
      let totalTaxable = 0.0;
      this.localDetails.sub_hire_job_charges.forEach((charge) => {
        if (charge.rate) {
          totalTaxable += (parseFloat(charge.rate) * parseFloat(charge.tax_percentage || 0)) / 100;
        }
      });
      if (this.localDetails.rate) {
        totalTaxable +=
          (parseFloat(this.localDetails.rate) *
            parseFloat(this.localDetails.tax_percentage || 0)) /
          100;
        totalTaxable = totalTaxable * this.legCount;
      }
      this.localDetails.total_taxable_amount = totalTaxable.toFixed(2);
      this.localDetails.total_cost_with_tax = (
        parseFloat(this.localDetails.total_cost || 0) +
        parseFloat(this.localDetails.total_taxable_amount)
      ).toFixed(2);
    },

    save() {
      this.$emit("save", this.localDetails);
      this.showDialog = false;
    },
  },
};
</script>
